// src/pages/UploadPage.tsx
//
// ★★★ WAVE 1a — ANNEX A STORIES 2, 3 AND 4, FROM A KEYBOARD.
//
// Until this page existed, a bill entered PRS 2.0 only through
// `scripts/ingest-corpus.mjs`, so Journey 1 of `docs/delivery/DEMO-RUNBOOK.md`
// began at a shell prompt and `docs/delivery/DEMO-READINESS-PLAN.md` §2 listed
// "no upload control exists on any screen" as the second thing a customer hits.
//
// ══ ⚠ WHAT THIS PAGE MUST NEVER IMPLY ══
//
//  1. **That anything was scanned.** `malwareScanning` comes from the server
//     and is `false` on every deployment this repository ships. The notice below
//     is rendered from that field, never from a constant here, so it cannot go
//     stale the way `NOT_SENT_NOTICE` did (`docs/HANDOFF.md` §2.2 — *"a stale
//     honesty notice is worse than none; it is a claim the code contradicts"*).
//  2. **That this is the file exchange.** The bound is small on purpose and
//     P2C1 §15's 4,879 records a day arrive over SFTP/CFT. The server sends the
//     sentence; this page shows it rather than writing its own.
//  3. **That "no channels" means "you are not allowed".** A deployment that
//     wired no inbox at all and a caller whose grants cover none of the wired
//     ones are two different situations with two different next actions, and
//     `configuredChannelCount` is what separates them. Four dashboard bands
//     were once filed as bugs for exactly this confusion.
//  4. **That a REJECTED delivery is a failed upload.** The request succeeded,
//     the delivery is recorded, and its verdict is a fact about the file. The
//     result panel says which of the two happened in its own words.
import { plainProse } from '../lib/plain-words'
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { REFUSAL_REMEDY, type RefusalKind } from '@prs2/contracts'
import { targetValue } from '../lib/sgds-dom'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { ConfirmWrite } from '../components/ConfirmWrite'
import type { InboundUploadResponse } from '../lib/types'
import { NETWORK_FAILURE } from '../lib/messages'

/** What each file-level outcome means, in the words an operator needs. */
const OUTCOME_NOTE: Readonly<Record<InboundUploadResponse['outcome'], string>> = {
  PROCESSED:
    'The delivery was read and every record in it was answered. Some records inside may still have been ' +
    'rejected: the counts below say which.',
  REJECTED:
    'The delivery itself was untrustworthy, so no record in it was applied. Nothing was created and nothing ' +
    'was changed.',
  DUPLICATE:
    'These exact bytes had already been delivered for this counterparty, so the original delivery’s outcome ' +
    'stands and nothing was processed a second time. The counts below are that original delivery’s.',
  UNATTRIBUTABLE:
    'No counterparty is registered on that channel, so nothing was read into the system. This is a ' +
    'configuration matter for PSD, not something to correct in the file.',
  NOT_IN_SCOPE:
    'Your identity holds no grant for the counterparty this channel is registered to. Nothing was read.',
  LEASED_ELSEWHERE:
    'This delivery is already claimed and nothing was done twice. That is usually a colleague processing ' +
    'the same file, but it is also what you see if your OWN earlier upload of these bytes failed part-way: ' +
    'its claim survives the failure for up to five minutes. If nobody else is sending this, wait five ' +
    'minutes and try again.',
  INTERRUPTED:
    'Something failed part-way through. The records already applied stand, the delivery stays claimed, and it ' +
    'resumes exactly where it stopped when the channel is next processed.',
}

/** Outcomes where a bad thing happened to the delivery, for the panel's tone. */
const ADVERSE: ReadonlySet<InboundUploadResponse['outcome']> = new Set([
  'REJECTED',
  'UNATTRIBUTABLE',
  'NOT_IN_SCOPE',
  'INTERRUPTED',
])

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`
  return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`
}

export function UploadPage() {
  const channels = useApi(() => api.inboundChannels(), [])
  const [selectedChannel, setSelectedChannel] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<InboundUploadResponse | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const usable = channels.data?.channels ?? []

  // Pick the only channel when there is only one, so the common case is
  // choose-a-file-and-send rather than choose-a-thing-there-is-one-of.
  useEffect(() => {
    if (selectedChannel === '' && usable.length === 1) setSelectedChannel(usable[0]!.channelId)
  }, [usable, selectedChannel])

  const bound = channels.data?.maxUploadBytes ?? 0
  // ⚠ Checked here so the refusal is immediate and names the file, AND on the
  // server, which is where it binds. This is a courtesy, never the control —
  // the server refuses at the content-type parser before a byte is buffered.
  const tooLarge = file !== null && bound > 0 && file.size > bound

  /**
   * ★★ WAVE 26 — "Upload and process" FREEZES a sentence and asks; the write
   * happens only from the confirmation (`ConfirmWrite`'s header says why every
   * irreversible write does this now). Processing a delivery creates bills and
   * adjustments that are validated and priced exactly as a provider's file would
   * be — there is no draft state to back out of. The sentence names the file,
   * its size and the counterparty it will be attributed to, composed once from
   * the fields as they were when the button was pressed.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  function askToProcess(): void {
    if (file === null || selectedChannel === '' || tooLarge) return
    const channel = usable.find((c) => c.channelId === selectedChannel)
    const attribution =
      channel === undefined
        ? `on channel ${selectedChannel}`
        : `on channel ${channel.channelId}, attributed to ${channel.counterpartyCode} (${channel.counterpartyKind})`
    setConfirming(
      `Process ${file.name} (${formatBytes(file.size)}) as a delivery ${attribution}. Processing creates the bills, ` +
        'upward adjustments and downward adjustments in the file, validated and priced as a provider delivery: ' +
        'accepted records take effect at once. There is no undo. A mistake is corrected by another record.',
    )
  }

  async function performWrite(): Promise<void> {
    setConfirming(null)
    if (file === null || selectedChannel === '') return
    setSending(true)
    setResult(null)
    setFailure(null)
    try {
      setResult(await api.uploadInboundFile(selectedChannel, file))
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError
          ? error.message
          : `${NETWORK_FAILURE} Nothing was uploaded.`,
      )
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Upload a delivery</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Send a <span className="pt-mono">PRS2-HSP-BILL</span> file for one of the counterparties your active
          identity covers. The file is placed on that counterparty&rsquo;s channel and read by the same intake a
          delivery arriving over the file exchange goes through: new bills, upward adjustments and downward
          adjustments alike.
        </p>
      </div>

      {channels.error !== null ? (
        <ErrorCard title="Could not load upload channels" message={channels.error} onRetry={channels.reload} />
      ) : channels.initialLoading ? (
        <LoadingCard label="Loading upload channels" rows={3} />
      ) : usable.length === 0 ? (
        // ★ The two empty states are DIFFERENT, and saying so is the point.
        channels.data!.configuredChannelCount === 0 ? (
          <EmptyCard
            title="This deployment has no upload channel"
            message={
              'No inbound channel is wired here, so there is nothing to upload onto and no action on this screen ' +
              'will change that. Deliveries reach this environment over the file exchange instead. A deployment ' +
              'enables this surface by setting up an inbound folder and its channels.'
            }
          />
        ) : (
          <EmptyCard
            title="No upload channel is yours"
            message={
              `This deployment wires ${channels.data!.configuredChannelCount} inbound channel(s), and your active ` +
              'identity holds no grant for the counterparty any of them is registered to. Switching to an identity ' +
              'with that grant is what changes this: nothing about the file matters yet.'
            }
          />
        )
      ) : (
        <>
          <div className="pt-card flex flex-col gap-4">
            {usable.length > 1 && (
              // ★ WAVE 26 — the house `SgdsSelect`, not a native `<select>`: one
              // dropdown across the console (sgds-reconciliation §1a's rule for
              // dates, applied to pickers).
              <SgdsSelect
                label="Channel"
                value={selectedChannel}
                onSgdsChange={(e) => {
                  setSelectedChannel(targetValue(e))
                  // A frozen sentence names a channel; a new choice makes it stale.
                  setConfirming(null)
                }}
              >
                <SgdsSelectOption value="">Choose a channel&hellip;</SgdsSelectOption>
                {usable.map((channel) => (
                  <SgdsSelectOption key={channel.channelId} value={channel.channelId}>
                    {channel.channelId}, {channel.counterpartyCode} ({channel.counterpartyKind})
                  </SgdsSelectOption>
                ))}
              </SgdsSelect>
            )}
            {usable.length === 1 && (
              <p className="text-sm text-[var(--sgds-body-color-subtle)]">
                Channel <span className="pt-mono">{usable[0]!.channelId}</span>, registered to{' '}
                <strong>{usable[0]!.counterpartyCode}</strong> ({usable[0]!.counterpartyKind}).
              </p>
            )}

            <label className="flex flex-col gap-1">
              <span className="font-semibold">File</span>
              <input
                type="file"
                className="pt-input"
                aria-describedby="upload-bound-note"
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null)
                  setResult(null)
                  setFailure(null)
                  // A frozen sentence names a file; a new choice makes it stale.
                  setConfirming(null)
                }}
              />
            </label>
            <p id="upload-bound-note" className="text-sm text-[var(--sgds-body-color-subtle)]">
              Up to <strong>{formatBytes(bound)}</strong> (about {channels.data!.maxUploadRecords} records).{' '}
              {plainProse(channels.data!.bulkDeliveryNote)}
            </p>

            {tooLarge && (
              <p role="alert" className="text-sm font-semibold text-[var(--sgds-danger-color-emphasis)]">
                {file!.name} is {formatBytes(file!.size)}, over the {formatBytes(bound)} bound. Split it, or send it
                on the file exchange.
              </p>
            )}

            <div>
              <SgdsButton
                type="button"
                disabled={file === null || selectedChannel === '' || sending || tooLarge || confirming !== null}
                onClick={askToProcess}
              >
                {sending ? 'Uploading…' : 'Upload and process'}
              </SgdsButton>
            </div>
          </div>

          {/*
            ★ THE HONESTY NOTICE, derived from the server's own capability.
            `malwareScanning` is `false` in every environment this repository
            ships (P2C11 §12.17-§12.19 is Bucket B), and this must never read as
            "your file was checked".
          */}
          <div className="pt-card">
            <p className="text-sm text-[var(--sgds-body-color-subtle)]">
              <strong>{channels.data!.malwareScanning ? 'Files are scanned.' : 'Files are not scanned.'}</strong>{' '}
              {plainProse(channels.data!.malwareScanningNote)}
            </p>
          </div>
        </>
      )}

      {confirming !== null && (
        <ConfirmWrite
          summary={confirming}
          confirmLabel="Yes, process it"
          variant="danger"
          onConfirm={() => void performWrite()}
          onCancel={() => setConfirming(null)}
        />
      )}

      {failure !== null && (
        <div role="alert">
          <ErrorCard title="The upload was refused" message={failure} />
        </div>
      )}

      {result !== null && (
        <div className="pt-card" role="status" aria-live="polite">
          <p
            className={
              ADVERSE.has(result.outcome)
                ? 'font-semibold text-[var(--sgds-danger-color-emphasis)]'
                : 'font-semibold text-[var(--sgds-success-color-emphasis)]'
            }
          >
            {result.outcome === 'PROCESSED' ? 'Delivery processed' : `Delivery ${result.outcome.toLowerCase()}`}
          </p>
          <p className="mt-2 text-[var(--sgds-body-color-subtle)] max-w-[70ch]">{OUTCOME_NOTE[result.outcome]}</p>

          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 max-w-[40rem]">
            <dt className="text-[var(--sgds-body-color-subtle)]">Counterparty</dt>
            <dd>{result.counterpartyCode}</dd>
            <dt className="text-[var(--sgds-body-color-subtle)]">Records read</dt>
            <dd>{result.parsed}</dd>
            <dt className="text-[var(--sgds-body-color-subtle)]">Accepted</dt>
            <dd>{result.accepted}</dd>
            <dt className="text-[var(--sgds-body-color-subtle)]">Rejected</dt>
            <dd>{result.rejected}</dd>
            <dt className="text-[var(--sgds-body-color-subtle)]">Size</dt>
            <dd>{formatBytes(result.bytes)}</dd>
          </dl>

          {result.reasonKind !== null && (
            <p className="mt-4">
              <strong>Why:</strong>{' '}
              {REFUSAL_REMEDY[result.reasonKind as RefusalKind] ?? result.reasonKind}
            </p>
          )}

          {/*
            ⚠ NOT a claim that the file was scanned — the server's own answer,
            repeated. A screen that quietly dropped this line on a successful
            upload would be the stale-honesty-notice defect again.
          */}
          {!result.malwareScanned && (
            <p className="mt-4 text-sm text-[var(--sgds-body-color-subtle)]">{plainProse(result.malwareScanNote)}</p>
          )}

          {/*
            ★★ WAVE 8 (Task 8) — THE DELIVERY ID, ON SCREEN, WITH SOMEWHERE TO
            TAKE IT.

            ⚠ `fileId` has been in this response since Wave 1a and this page
            never rendered it, because until Task 8 there was nothing an officer
            could DO with it: `POST /api/outcome-files` existed and no screen
            reached it, so the only way to answer a delivery was a shell. That is
            what made the outcome leg undemonstrable — a demonstration is a
            person clicking.

            ⚠ Rendered ONLY for a delivery that can actually be answered, and
            the condition is the honest one rather than a convenient one:
            `fileId` is `null` for `UNATTRIBUTABLE`/`NOT_IN_SCOPE` (never
            claimed), and `PROCESSED` is the only state
            `ANSWERABLE_INBOUND_STATE` admits (A-55) — so offering the id for a
            `REJECTED` or `INTERRUPTED` delivery would send an officer to a
            control that can only refuse them. The id is still recoverable from
            the audit trail for those; this line is a shortcut, not the record.
          */}
          {result.outcome === 'PROCESSED' && result.fileId !== null && (
            <div className="mt-4">
              <p className="m-0 text-sm text-[var(--sgds-body-color-subtle)]">This delivery&rsquo;s id</p>
              <p className="pt-mono m-0 break-all">{result.fileId}</p>
              <p className="mt-1 text-sm max-w-[70ch]">
                <Link to="/settlement-batches">Answer this delivery with an outcome file &rarr;</Link>{' '}
                <span className="text-[var(--sgds-body-color-subtle)]">
                  Producing one creates an artefact inside PRS and sends nothing; that page states what does and
                  does not reach the provider.
                </span>
              </p>
            </div>
          )}

          <p className="mt-4">
            <Link to="/records">See what happened to each record →</Link>
          </p>
        </div>
      )}
    </div>
  )
}
